import { useRef, useEffect, useMemo, useCallback } from 'react';
import Debug from 'debug';

import { reportErrorClient } from '~/app/lib/sentry/client';
import { tryParseJson } from '~/app/lib/utils/object';
import useHash from '~/app/lib/hooks/useHash';

import {
  useAppAlert,
  useAppLoading,
} from '~/app/components/NextApp/lib/CoreUi';

import { ServiceAuthProps } from '../types';

import loadMusicKit, { MusicKitInstance } from './loadMusicKit';

const debug = Debug('songwhip/useAppleMusicAuth');

export type AppleMusicAuthResultSuccess = {
  email?: string;
  token: string;
};

export type AppleMusicAuthProps = ServiceAuthProps<AppleMusicAuthResultSuccess>;

export const useAppleMusicAuth = ({
  enabled = true,

  onSuccess,
  onError,
}: AppleMusicAuthProps & {
  /**
   * When 'enabled' (default) MusicKit.js will be loaded
   * and token fetched from our backend. Means you can use
   * the hook but conditionally enable it. Basically a
   * workaround as you can't put hooks in `if` blocks.
   */
  enabled?: boolean;
}) => {
  const userTokenFromUrl = useResolveAppleMusicUserTokenFromUrl();
  const musicKitRef = useRef<MusicKitInstance>();

  const { hashParams, setHashParam } = useHash();
  const setAppLoading = useAppLoading();
  const appAlert = useAppAlert();

  // we must preload musicKit so it's ready when user triggers .start()
  // using `useMemo` to kick-off request, asap before mount
  useMemo(async () => {
    // don't run if caller has disabled
    if (!enabled || !process.browser) {
      return;
    }

    // show the app loading bar to indicate stuff is loading behind the scenes
    setAppLoading(true);

    musicKitRef.current = await loadMusicKit({
      isOrchardPage: true,
    });

    setAppLoading(false);
  }, [enabled]);

  useEffect(() => {
    if (!userTokenFromUrl) return;

    debug('got auth callback result from hash');

    onUserToken({
      // we supposed to add email to hash before starting the auth flow
      // to handle webview edge case when songwhip page is reloaded
      email: hashParams.email,
      userToken: userTokenFromUrl,
    });
  }, [userTokenFromUrl]);

  const onUserToken = useCallback(
    async ({ email, userToken }: { email?: string; userToken: string }) => {
      debug('got user token', userToken);

      onSuccess({
        email,
        token: userToken,
      });
    },
    []
  );

  return useCallback(
    async (email?: string) => {
      if (email) {
        // we want to keep email in the hash for webview fallback
        setHashParam({ email }, { replace: true });
      }

      try {
        // COMPLEX: we may want to improve this to retry loading the library until we can
        // succeed. Although be aware that Apple only opens the popup if invoked
        // synchronously from a 'trusted' click (ie. directly in a UI event handler).
        if (!musicKitRef.current) {
          appAlert({
            content: 'Apple Music SDK not loaded, please retry',
          });

          return;
        }

        // NOTE: if the popup window is manually closed this promise never resolves
        // UPDATE: this is now handled in the MusicKit.js library
        const userToken = await musicKitRef.current?.authorize();

        onUserToken({
          email,
          userToken,
        });
      } catch (error) {
        if (shouldHandleAppleMusicError(error)) {
          const message = 'Apple Music error';

          reportErrorClient({
            error: new Error(message),
            extras: { error },
          });

          onError?.(message);
        }
      }
    },
    [enabled]
  );
};

/**
 * When in android/ios webview MusicKit.js will navigate away from songwhip.com
 * to apple.com and then back to original page appending the auth result in
 * the url hash fragment. This hook handles this case and parses the user token
 * from the hash.
 */
export const useResolveAppleMusicUserTokenFromUrl = () => {
  const { initialDupeFragment, hash } = useHash();
  const potentialAuthResult = initialDupeFragment || hash;

  return tryParseUserToken(potentialAuthResult);
};

const tryParseUserToken = (string: string | undefined) => {
  try {
    if (string) {
      return tryParseJson<{
        itre?: '0';
        musicUserToken?: string;
      }>(atob(string))?.musicUserToken;
    }
  } catch (e) {}
};

const shouldHandleAppleMusicError = (error: unknown) => {
  if (error instanceof Error) {
    // https://js-cdn.music.apple.com/musickit/v3/docs/index.html?path=/story/reference-javascript-mkerror--page#authorization_error
    const rejectedAuthErrorCode = 'AUTHORIZATION_ERROR';
    return !error.message.includes(rejectedAuthErrorCode);
  }

  return true;
};
